import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Layers,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  X,
  Loader2,
  Search,
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { getAsset } from '@/client/utils/assets';
import Pagination from '@/client/components/common/Pagination';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useCollectionsQuery,
  useCollectionEntitiesQuery,
  useCreateCollectionMutation,
  useUpdateCollectionMutation,
  useDeleteCollectionMutation,
  useAddEntityToCollectionMutation,
  useRemoveEntityFromCollectionMutation,
  AdminCollection,
} from '@/client/api/listing-management/collections.query';

type Owner = { kind: 'uid' | 'oid'; id: number };

// ─── Create / edit dialog ────────────────────────────────────────────────────

const CollectionDialog = ({
  open,
  owner,
  target,
  onClose,
}: {
  open: boolean;
  owner: Owner;
  target: AdminCollection | null;
  onClose: () => void;
}) => {
  const isEdit = !!target;
  const [form, setForm] = useState({
    name: target?.name ?? '',
    description: target?.description ?? '',
    color: target?.color ?? '',
    icon: target?.icon ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateCollectionMutation();
  const updateMutation = useUpdateCollectionMutation();
  const pending = createMutation.isPending || updateMutation.isPending;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      if (isEdit && target?.ecolid) {
        await updateMutation.mutateAsync({
          ecolid: target.ecolid,
          fields: {
            name: form.name.trim(),
            description: form.description.trim() || undefined,
            color: form.color.trim() || undefined,
            icon: form.icon.trim() || undefined,
          },
        });
        toast.success('Collection updated');
      } else {
        await createMutation.mutateAsync({
          [owner.kind]: owner.id,
          name: form.name.trim(),
          description: form.description.trim() || undefined,
          color: form.color.trim() || undefined,
          icon: form.icon.trim() || undefined,
        } as any);
        toast.success('Collection created');
      }
      onClose();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save the collection.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-indigo-600" />
            {isEdit ? 'Edit collection' : 'New collection'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="col-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="col-name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="col-desc">Description</Label>
            <Input
              id="col-desc"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="col-color">Color</Label>
              <Input
                id="col-color"
                placeholder="#4f46e5"
                value={form.color}
                onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="col-icon">Icon</Label>
              <Input
                id="col-icon"
                placeholder="optional"
                value={form.icon}
                onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
              />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Create collection'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ─── Collection entities view ────────────────────────────────────────────────

const CollectionEntities = ({
  collection,
  onBack,
}: {
  collection: AdminCollection;
  onBack: () => void;
}) => {
  const ecolid = collection.ecolid!;
  const [eidInput, setEidInput] = useState('');
  const { data, isLoading } = useCollectionEntitiesQuery(ecolid);
  const addMut = useAddEntityToCollectionMutation();
  const removeMut = useRemoveEntityFromCollectionMutation();

  // The endpoint returns the listings under `items` ({ total, page, per_page,
  // items }); older/bare shapes used `entities` or a top-level array — accept
  // all three so the list renders instead of falling back to the empty state.
  const body: any = data?.data && typeof data.data === 'object' ? data.data : data;
  const entities: any[] = Array.isArray(body?.items)
    ? body.items
    : Array.isArray(body?.entities)
      ? body.entities
      : Array.isArray(body)
        ? body
        : [];

  const add = async () => {
    const eid = Number(eidInput.trim());
    if (!eid) return;
    try {
      await addMut.mutateAsync({ ecolid, eid });
      toast.success(`Listing #${eid} added`);
      setEidInput('');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to add the listing.');
    }
  };

  const remove = async (eid: number) => {
    try {
      await removeMut.mutateAsync({ ecolid, eid });
      toast.success(`Listing #${eid} removed`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to remove the listing.');
    }
  };

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="h-4 w-4" /> Back to collections
      </button>
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-1 text-lg font-semibold text-gray-900">{collection.name}</h2>
        {collection.description && <p className="mb-4 text-sm text-gray-500">{collection.description}</p>}

        <div className="mb-4 flex items-center gap-2">
          <Input
            className="max-w-xs"
            inputMode="numeric"
            placeholder="Add listing by eid…"
            value={eidInput}
            onChange={(e) => setEidInput(e.target.value)}
          />
          <Button onClick={add} disabled={!eidInput.trim() || addMut.isPending}>
            {addMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
          </Button>
        </div>

        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : entities.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">No listings in this collection.</p>
        ) : (
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
            {entities.map((e: any) => {
              // The listing's eid lives on the nested Entity; `e.id` is the
              // collection-map row id, so it must NOT be used for removal.
              const eid = e.Entity?.eid ?? e.eid;
              const ent = e.Entity ?? e;
              const name =
                ent.name_basic || ent.name_marketing || ent.name || `Listing #${eid}`;
              const cover = getAsset(e.Assets?.[0] ?? ent.Assets?.[0]);
              const price = e.Prices?.[0]?.price ?? ent.Prices?.[0]?.price;
              const ccode = e.Prices?.[0]?.ccode ?? ent.Prices?.[0]?.ccode ?? '';
              return (
                <div key={e.id ?? eid} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      {cover ? (
                        <img src={cover} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-300">
                          <Layers className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-gray-800">{name}</p>
                      <p className="text-xs text-gray-400">
                        eid {eid}
                        {price != null ? ` · ${ccode} ${Number(price).toLocaleString()}` : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => remove(eid)}
                    disabled={eid == null || removeMut.isPending}
                    className="flex-shrink-0 rounded-md p-1.5 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const CollectionsPage = () => {
  useBreadcrumbs([
    { label: 'Listing Management', href: '#' },
    { label: 'Collections' },
  ]);

  // Browse ALL collections by default, with free-text search + pagination.
  // Search, pagination and the applied owner scope all persist in the URL
  // (?q=&page=&limit=&ownerKind=&ownerId=) so a reload restores the same view.
  const {
    filters,
    setFilters,
    searchTerm,
    debouncedSearchTerm,
    handleSearch,
    page,
    limit,
    handlePageChange,
    handleLimitChange,
  } = useListFilters<{ ownerKind: string; ownerId: string }>({
    initialFilters: { ownerKind: '', ownerId: '' },
    initialLimit: 12,
    debounceMs: 400,
  });
  const search = searchTerm;
  const debouncedSearch = debouncedSearchTerm.trim();
  const setSearch = handleSearch;
  const setPage = handlePageChange;
  const owner: Owner | null = filters.ownerId
    ? { kind: (filters.ownerKind as 'uid' | 'oid') || 'uid', id: Number(filters.ownerId) }
    : null;

  // Owner-scope input controls (transient until "applied" via the form).
  const [ownerKind, setOwnerKind] = useState<'uid' | 'oid'>('uid');
  const [ownerIdInput, setOwnerIdInput] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminCollection | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AdminCollection | null>(null);
  const [viewing, setViewing] = useState<AdminCollection | null>(null);

  const { data, isLoading, isError, isFetching } = useCollectionsQuery(
    owner
      ? { [owner.kind]: owner.id }
      : { search: debouncedSearch || undefined, page, page_size: limit }
  );
  const deleteMutation = useDeleteCollectionMutation();

  const collections = data?.collections ?? [];
  const total = data?.total ?? collections.length;

  const applyOwner = (e: React.FormEvent) => {
    e.preventDefault();
    const id = Number(ownerIdInput.trim());
    if (!id) {
      toast.error('Enter a numeric id.');
      return;
    }
    setViewing(null);
    setFilters({ ownerKind, ownerId: String(id) });
    setPage(1);
  };

  const clearOwner = () => {
    setFilters({ ownerId: '' });
    setOwnerIdInput('');
    setPage(1);
  };

  const handleDelete = async () => {
    if (!pendingDelete?.ecolid) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.ecolid);
      toast.success('Collection deleted');
      setPendingDelete(null);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete the collection.');
    }
  };

  const ownerLabel = (c: AdminCollection) =>
    c.oid ? `org ${c.oid}` : c.uid ? `uid ${c.uid}` : '—';

  // The create/edit dialog needs an owner: use the applied scope, or (when
  // editing while browsing all) derive it from the collection being edited.
  const dialogOwner: Owner | null =
    owner ??
    (editing?.oid != null
      ? { kind: 'oid', id: editing.oid }
      : editing?.uid != null
        ? { kind: 'uid', id: editing.uid }
        : null);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center gap-3">
          <Layers className="h-7 w-7 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Collections</h1>
            <p className="text-sm text-gray-500">
              Browse every user- and org-owned listing collection. Search, or scope
              to a specific owner to create and manage.
            </p>
          </div>
        </div>

        {/* Toolbar: search + optional owner scope */}
        {!viewing && (
          <div className="mb-6 space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[240px] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  className="pl-9"
                  placeholder="Search collections by name or description…"
                  value={search}
                  disabled={!!owner}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {isFetching && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
              {owner && (
                <Button
                  type="button"
                  variant="outline"
                  className="gap-2"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" /> New collection
                </Button>
              )}
            </div>

            <form onSubmit={applyOwner} className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
              <span className="text-xs font-medium text-gray-500">Filter by owner:</span>
              <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-1">
                {(['uid', 'oid'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setOwnerKind(k)}
                    className={`rounded-md px-3 py-1 text-xs font-medium uppercase transition-colors ${
                      ownerKind === k ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {k}
                  </button>
                ))}
              </div>
              <Input
                className="max-w-[160px]"
                inputMode="numeric"
                placeholder={`Enter ${ownerKind}…`}
                value={ownerIdInput}
                onChange={(e) => setOwnerIdInput(e.target.value)}
              />
              <Button type="submit" size="sm" variant="outline" disabled={!ownerIdInput.trim()}>
                Apply
              </Button>
              {owner && (
                <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                  {owner.kind} {owner.id}
                  <button type="button" onClick={clearOwner} className="text-indigo-500 hover:text-indigo-700">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
            </form>
          </div>
        )}

        {viewing ? (
          <CollectionEntities collection={viewing} onBack={() => setViewing(null)} />
        ) : isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-xl" />
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            Could not load collections.
          </div>
        ) : collections.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center text-sm text-gray-400">
            {owner
              ? `No collections for this ${owner.kind}.`
              : debouncedSearch
                ? 'No collections match your search.'
                : 'No collections found.'}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {collections.map((c) => (
                <div key={c.ecolid} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="mb-2 flex items-start justify-between">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-lg text-white"
                      style={{ background: c.color || '#4f46e5' }}
                    >
                      <Layers className="h-4 w-4" />
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          setEditing(c);
                          setDialogOpen(true);
                        }}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setPendingDelete(c)}
                        className="rounded p-1.5 text-rose-600 hover:bg-rose-50"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">{c.name}</h3>
                  {c.description && <p className="mt-1 line-clamp-2 text-xs text-gray-500">{c.description}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
                      {ownerLabel(c)}
                    </span>
                    <button
                      onClick={() => setViewing(c)}
                      className="text-xs font-medium text-indigo-600 hover:underline"
                    >
                      Manage listings →
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination only in the unscoped (all-collections) browse mode. */}
            {!owner && total > limit && (
              <div className="mt-6 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <Pagination
                  total={total}
                  page={page}
                  itemsPerPage={limit}
                  limit={limit}
                  setPage={setPage}
                  setItemsPerPage={handleLimitChange}
                  showButtons
                  showSelect
                  selectOptions={[12, 24, 48, 96]}
                />
              </div>
            )}
          </>
        )}
      </div>

      {dialogOpen && dialogOwner && (
        <CollectionDialog
          key={editing?.ecolid ?? 'create'}
          open={dialogOpen}
          owner={dialogOwner}
          target={editing}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
        />
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the collection. Listings themselves are not deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={deleteMutation.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CollectionsPage;
